import {
  type FetchOneInsightInboxDto,
  InsightEnergyTypeEnum,
  InsightInboxCategoryEnum,
  InsightInboxGroupByEnum,
  type InsightInboxGroupedResponseListDto,
  InsightInboxPriorityEnum,
  type InsightInboxStatusEnum,
} from '@/src/api/types/generated-types'
import { asFiniteNumber, formatInsightEur } from '@/src/features/insight-inbox/categories/insightPresentation'
import type { InsightListSortParam } from '@/src/features/insight-inbox/types/InsightInbox.types'
import { INSIGHTS_BASE_ROUTE, TABS } from '@/src/utils/constants'
import { isSameDay } from 'date-fns'
import type { TFunction } from 'i18next'
import type { IconType } from 'react-icons'
import { LuLightbulb, LuLineChart, LuMoon, LuScale, LuUnplug, LuZap } from 'react-icons/lu'
import { MdOutlineLocationOn, MdOutlineSpeed } from 'react-icons/md'

type InsightInboxListResponse = { insights: FetchOneInsightInboxDto[] } | InsightInboxGroupedResponseListDto

export const formatInsightAmount = formatInsightEur

export const sumInsightSavingsEur = (insights: FetchOneInsightInboxDto[]): number =>
  insights.reduce((sum, insight) => sum + Math.max(asFiniteNumber(insight.savingsEurAnalysisWindow) ?? 0, 0), 0)

export const formatInsightCreatedAt = (createdAt: string | Date, t: TFunction): string => {
  const date = new Date(createdAt)
  return isSameDay(date, new Date())
    ? t('common.today')
    : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export type InsightTab = 'new' | 'investigation' | 'assignedToMe' | 'convertedToMeasure' | 'resolved' | 'dismissed' | 'notReleased'

export const NOT_RELEASED_TAB: InsightTab = 'notReleased'

export enum InsightInboxSortValue {
  NEWEST_FIRST = 'newest_first',
  OLDEST_FIRST = 'oldest_first',
  PRIORITY_HIGH_FIRST = 'priority_high_first',
  PRIORITY_LOW_FIRST = 'priority_low_first',
}

export const INSIGHT_TABS: InsightTab[] = [
  'new',
  'investigation',
  'assignedToMe',
  'convertedToMeasure',
  'resolved',
  'dismissed',
  'notReleased',
]

export const INSIGHT_STATUS_BY_TAB: Record<
  InsightTab,
  Array<'NEW' | 'DISMISSED' | 'INVESTIGATION' | 'CONVERTED_TO_MEASURE' | 'RESOLVED'>
> = {
  new: ['NEW'],
  investigation: ['INVESTIGATION'],
  assignedToMe: [],
  convertedToMeasure: ['CONVERTED_TO_MEASURE'],
  resolved: ['RESOLVED'],
  dismissed: ['DISMISSED'],
  notReleased: [],
}

export const INSIGHT_TAB_BY_STATUS = Object.fromEntries(
  INSIGHT_TABS.flatMap(tab => INSIGHT_STATUS_BY_TAB[tab].map(status => [status, tab] as const))
) as Record<InsightInboxStatusEnum, InsightTab>

export const INSIGHT_TAB_ROUTE: Record<InsightTab, string> = {
  new: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.new}`,
  investigation: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.investigation}`,
  assignedToMe: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.assignedToMe}`,
  convertedToMeasure: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.convertedToMeasure}`,
  resolved: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.resolved}`,
  dismissed: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.dismissed}`,
  notReleased: `${INSIGHTS_BASE_ROUTE}/${TABS.insightInbox.notReleased}`,
}

export const ROUTE_TO_TAB: Record<string, InsightTab> = {
  [TABS.insightInbox.new]: 'new',
  [TABS.insightInbox.investigation]: 'investigation',
  [TABS.insightInbox.assignedToMe]: 'assignedToMe',
  [TABS.insightInbox.convertedToMeasure]: 'convertedToMeasure',
  [TABS.insightInbox.resolved]: 'resolved',
  [TABS.insightInbox.dismissed]: 'dismissed',
  [TABS.insightInbox.notReleased]: 'notReleased',
}

export const getInsightInboxSortOptions = (t: TFunction) => [
  { value: InsightInboxSortValue.NEWEST_FIRST, label: t('insightInbox.sort.newest_first') },
  { value: InsightInboxSortValue.OLDEST_FIRST, label: t('insightInbox.sort.oldest_first') },
  { value: InsightInboxSortValue.PRIORITY_HIGH_FIRST, label: t('insightInbox.sort.priority_high_first') },
  { value: InsightInboxSortValue.PRIORITY_LOW_FIRST, label: t('insightInbox.sort.priority_low_first') },
]

export const getInsightSortQuery = (sortOption?: InsightInboxSortValue): InsightListSortParam => {
  switch (sortOption) {
    case InsightInboxSortValue.OLDEST_FIRST:
      return { sortBy: 'createdAt', sortOrder: 'ASC' }
    case InsightInboxSortValue.PRIORITY_HIGH_FIRST:
      return { sortBy: 'priority', sortOrder: 'DESC' }
    case InsightInboxSortValue.PRIORITY_LOW_FIRST:
      return { sortBy: 'priority', sortOrder: 'ASC' }
    default:
      return { sortBy: 'createdAt', sortOrder: 'DESC' }
  }
}

// Grouping ---------------------------------------------------------------

export const getInsightInboxGroupOptions = (t: TFunction) => [
  { value: InsightInboxGroupByEnum.NONE, label: t('insightInbox.group.none') },
  { value: InsightInboxGroupByEnum.METER, label: t('insightInbox.group.meter') },
  { value: InsightInboxGroupByEnum.CATEGORY, label: t('insightInbox.group.category') },
  { value: InsightInboxGroupByEnum.LOCATION, label: t('insightInbox.group.location') },
]

// Gray subtitle appended to the header title reflecting the active grouping (empty when ungrouped).
export const getInsightGroupSubtitle = (groupBy: InsightInboxGroupByEnum, t: TFunction): string => {
  switch (groupBy) {
    case InsightInboxGroupByEnum.METER:
      return t('insightInbox.group.subtitle_meter')
    case InsightInboxGroupByEnum.CATEGORY:
      return t('insightInbox.group.subtitle_category')
    case InsightInboxGroupByEnum.LOCATION:
      return t('insightInbox.group.subtitle_location')
    default:
      return ''
  }
}

// For category grouping the server returns the raw category enum value as the group label; translate it for display.
export const getInsightCategoryLabel = (value: string, t: TFunction): string => {
  const known = Object.values(InsightInboxCategoryEnum).includes(value as InsightInboxCategoryEnum)
  return known ? t(`insightInbox.category.${value}`) : value
}

const INSIGHT_CATEGORY_ICONS: Record<InsightInboxCategoryEnum, IconType> = {
  [InsightInboxCategoryEnum.STANDBY_DETECTION]: LuMoon,
  [InsightInboxCategoryEnum.PEAKLOAD]: LuZap,
  [InsightInboxCategoryEnum.BASELOAD_BENCHMARK]: LuScale,
  [InsightInboxCategoryEnum.BASELOAD_TREND]: LuLineChart,
  [InsightInboxCategoryEnum.CONNECTION_LOSS]: LuUnplug,
}

const getInsightCategoryIcon = (value: string): IconType => INSIGHT_CATEGORY_ICONS[value as InsightInboxCategoryEnum] ?? LuLightbulb

export const getInsightGroupIcon = (groupBy: InsightInboxGroupByEnum, groupKey: string): IconType => {
  switch (groupBy) {
    case InsightInboxGroupByEnum.METER:
      return MdOutlineSpeed
    case InsightInboxGroupByEnum.LOCATION:
      return MdOutlineLocationOn
    default:
      return getInsightCategoryIcon(groupKey)
  }
}

// Filtering --------------------------------------------------------------

// The three multi-select groups of the filter menu. Each key maps 1:1 onto a query param of GET /insight-inbox.
export type InsightFilterKey = 'priorities' | 'categories' | 'energyTypes'
export type InsightFilterValue = InsightInboxPriorityEnum | InsightInboxCategoryEnum | InsightEnergyTypeEnum

export type InsightFilters = {
  priorities: InsightInboxPriorityEnum[]
  categories: InsightInboxCategoryEnum[]
  energyTypes: InsightEnergyTypeEnum[]
}

export type InsightFilterGroup = {
  key: InsightFilterKey
  label: string
  options: Array<{ value: InsightFilterValue; label: string }>
}

// Highest priority first — matches how the priority tags are ranked in the list.
const PRIORITY_ORDER = [InsightInboxPriorityEnum.HIGH, InsightInboxPriorityEnum.MEDIUM, InsightInboxPriorityEnum.LOW]

const FILTERABLE_CATEGORIES = [
  InsightInboxCategoryEnum.STANDBY_DETECTION,
  InsightInboxCategoryEnum.PEAKLOAD,
  InsightInboxCategoryEnum.CONNECTION_LOSS,
  InsightInboxCategoryEnum.BASELOAD_TREND,
  InsightInboxCategoryEnum.BASELOAD_BENCHMARK,
]

// Single source of truth for both the filter menu and the active-filter chips.
export const getInsightInboxFilterGroups = (t: TFunction): InsightFilterGroup[] => [
  {
    key: 'priorities',
    label: t('insightInbox.priority.label'),
    options: PRIORITY_ORDER.map(value => ({ value, label: t(`insightInbox.priority.${value.toLowerCase()}`) })),
  },
  {
    key: 'categories',
    label: t('insightInbox.filter.category'),
    options: FILTERABLE_CATEGORIES.map(value => ({ value, label: t(`insightInbox.category.${value}`) })),
  },
  {
    key: 'energyTypes',
    label: t('insightInbox.filter.energy_type'),
    options: Object.values(InsightEnergyTypeEnum).map(value => ({ value, label: t(`insightInbox.energy_type.${value}`) })),
  },
]

// Response shape helpers -------------------------------------------------

export const isGroupedInsightResponse = (data?: InsightInboxListResponse): data is InsightInboxGroupedResponseListDto =>
  !!data && 'groups' in data

// Flat list of insights regardless of response shape. Note: with meter or location grouping an insight can appear in several
// groups, so the returned array may contain duplicates — fine for "first insight" / non-empty checks.
export const getInsightsFromResponse = (data?: InsightInboxListResponse): FetchOneInsightInboxDto[] => {
  if (!data) return []
  return isGroupedInsightResponse(data) ? data.groups.flatMap(group => group.insights) : data.insights
}
